import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { VolumePage } from '../data/volumePages';
type PageLightboxProps = {
  pages: VolumePage[];
  startIndex: number;
  label?: string;
  onClose: () => void;
};
const navClass = 'absolute top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50 lg:flex';
export function PageLightbox({
  pages,
  startIndex,
  label = 'Pages',
  onClose
}: PageLightboxProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [index, setIndex] = useState(startIndex);
  const total = pages.length;
  const goTo = useCallback((i: number, smooth = true) => {
    const track = trackRef.current;
    if (!track) return;
    const target = (i % total + total) % total;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: target * track.clientWidth,
      behavior: smooth && !reduce ? 'smooth' : 'auto'
    });
  }, [total]);

  // Abrir directamente en la imagen tocada.
  useLayoutEffect(() => {
    goTo(startIndex, false);
  }, [goTo, startIndex]);
  useEffect(() => {
    closeRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goTo(index + 1, false);
      if (e.key === 'ArrowLeft') goTo(index - 1, false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goTo, index, onClose]);
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    setIndex(Math.round(track.scrollLeft / track.clientWidth));
  };
  return <motion.div role="dialog" aria-modal="true" aria-label={label} className="fixed inset-0 z-[100] flex flex-col bg-[#141826]/95" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} data-id="element-138" data-mp="1ftnibe6gg8hj">
      <div className="flex items-center justify-between px-4 py-3" data-id="element-139" data-mp="i6ofelp664b7">
        <p className="text-sm font-semibold tabular-nums text-white/80" aria-live="polite" data-id="element-140" data-mp="i8hqcd6tfap0">
          {index + 1} / {total}
        </p>
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50" data-id="element-141" data-mp="s42w8mf08v41">
          <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-142" data-mp="2b53jdt9qrpzo" />
        </button>
      </div>

      <div className="relative min-h-0 flex-1" data-id="element-143" data-mp="2g3es20oavugb">
        <div ref={trackRef} onScroll={handleScroll} className="flex h-full snap-x snap-mandatory overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{
        scrollbarWidth: 'none'
      }} data-id="element-144" data-mp="215cmjkxpkywf">
          {pages.map((page, i) => <div key={page.src} className="flex h-full w-full flex-none snap-center items-center justify-center px-4 pb-6" data-id="element-145" data-mp="2tvuogimp4u89" data-mpi={i}>
              <img src={page.src} alt={page.alt} loading={Math.abs(i - startIndex) <= 1 ? 'eager' : 'lazy'} className="max-h-full max-w-full rounded-2xl bg-white object-contain" data-id="element-146" data-mp="2k2x9gmgwxnee" />
            </div>)}
        </div>

        <button type="button" onClick={() => goTo(index - 1)} aria-label="Previous page" className={`${navClass} left-4`} data-id="element-147" data-mp="3hknn4mupdg6k">
          <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-148" data-mp="1tsoifkzygxcf" />
        </button>
        <button type="button" onClick={() => goTo(index + 1)} aria-label="Next page" className={`${navClass} right-4`} data-id="element-149" data-mp="1oud7f6xxcgsm">
          <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-150" data-mp="1jyepa136vv0n" />
        </button>
      </div>
    </motion.div>;
}